<script setup lang="ts">
import {onMounted, watch, computed} from 'vue';
import {storeToRefs} from 'pinia';
import {useCrmAccountStore, useValueDataStore} from '@/stores';
import {organisationFields} from '@/crm/spec/organisationFields';
import type {EntityName, FieldDef, SectionDef} from '@/crm/spec/types';
import FieldSection from '@/crm/components/FieldSection.vue';
import SimpleTable from '@/crm/components/SimpleTable.vue';
import Spinner from '@/components/common/Spinner.vue';

const valueStore = useValueDataStore();
const crmStore = useCrmAccountStore();
const {selectionId} = storeToRefs(valueStore);
const {account, reps, isAccountLoading, isRepsLoading, accountError} = storeToRefs(crmStore);

const fieldSections = computed(() => organisationFields.sections.filter(s => s.fields));
const repsSection: SectionDef = organisationFields.sections.find(s => s.entity === 'lon_salesrepassignment')!;
const repsTitle: string = repsSection.name ?? '';
const repsEntity: EntityName = repsSection.entity;
const repsColumns: FieldDef[] = repsSection.columns ?? [];

async function load() {
    await crmStore.ensureAccount();
    await crmStore.ensureReps();
}

onMounted(load);
watch(selectionId, load);
</script>

<template>
    <div class="crm-page">
        <div v-if="!selectionId" class="placeholder">Select a store or group to view its CRM record.</div>

        <template v-else>
            <div v-if="isAccountLoading && !account" class="loading"><Spinner/></div>
            <div v-else-if="accountError" class="error">Couldn't load account.</div>

            <template v-else-if="account">
                <FieldSection v-for="(s, i) in fieldSections" :key="i" :section="s" :record="account"/>

                <SimpleTable
                    :title="repsTitle"
                    :entity="repsEntity"
                    :columns="repsColumns"
                    :rows="reps"
                    :show-count="false"
                />
                <div v-if="isRepsLoading" class="loading-inline"><Spinner/></div>
            </template>
        </template>
    </div>
</template>

<style scoped>
.crm-page {
    padding: 1.5rem;
    max-width: 1400px;
    margin: 0 auto;
    color: var(--color-text);
}

.placeholder, .error {
    padding: 2rem;
    text-align: center;
    opacity: 0.7;
    font-size: var(--font-size-body);
}

.loading {
    display: flex;
    justify-content: center;
    padding: 3rem;
}

.loading-inline {
    display: flex;
    justify-content: center;
    padding: 0.5rem;
}
</style>
